import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { NoChat } from "./NoChat"
import { UsersList } from "./UsersList"
import styles from "./styles.module.scss"
import { BackButton } from "./BackButton"
import { DialogSide } from "./DialogSide"
import { ChatHeader } from "./ChatHeader"

import { Spinner } from "components/Spinner"
import { createChatWithNewMessage, getChatList } from "modules/Chat/utils/api"
import { chatSelector } from "utils/store/selectors/chat"
import { AppDispatch } from "utils/store/store"
import { useTabletOrMobile } from "utils/hooks"
import {
    MessageStatus,
    WebsocketActions,
    WebsocketChatMessage,
} from "modules/Chat/utils/interfaces"
import { useSocket } from "utils/contexts/SocketContext"
import { addMessages, updateChat, updateMessage } from "utils/store/slices"
import { Layout } from "components"
import { isBrowser } from "utils/infrastructure/ssg-utils"

export function ChatPage() {
    const { activeChatId, isLoading, chats, role } = useSelector(chatSelector)
    const socket = useSocket()
    const isTabletOrMobile = useTabletOrMobile()
    const dispatch = useDispatch<AppDispatch>()

    useEffect(() => {
        fetchChatList()
        isBrowser() && window.scrollTo(0, 0)
    }, [])

    const fetchChatList = () => {
        if (role) {
            void dispatch(getChatList(role))
        }
    }

    useEffect(() => {
        if (!socket) return

        socket.offReconnect(subscribeToSocketEvents)
        socket.onReconnect(subscribeToSocketEvents)

        subscribeToSocketEvents()

        return () => {
            socket.offMessage()
            socket.offReconnect(subscribeToSocketEvents)
        }
    }, [socket])

    const subscribeToSocketEvents = () => {
        if (!socket) return

        socket.offMessage()

        socket.onMessage((data: WebsocketChatMessage) => {
            switch (data.action) {
                case WebsocketActions.CREATED:
                    handleCreatedMessage(data)
                    break
                case WebsocketActions.VIEWED:
                    handleViewedMessage(data)
                    break
                case WebsocketActions.NEW:
                    handleNewMessage(data)
                    break
                default:
                    console.log("Неизвестное действие:", data.action)
            }
        })
    }

    const handleCreatedMessage = (data: WebsocketChatMessage) => {
        const timestamp = data.timestamp || new Date().toISOString()
        dispatch(
            updateMessage({
                hash: data.hash,
                id: data.id,
                timestamp,
                status: MessageStatus.CREATED,
            })
        )
        fetchChatList()
    }

    const handleViewedMessage = (data: WebsocketChatMessage) => {
        dispatch(
            updateMessage({
                id: data.id,
                chatId: data.chatId,
                status: MessageStatus.VIEWED,
            })
        )
        fetchChatList()
    }

    const handleNewMessage = (data: WebsocketChatMessage) => {
        if (!data.text || !data.timestamp || !data.externalUserId) return

        if (data.chatId === activeChatId) {
            dispatch(
                addMessages([
                    {
                        id: data.id,
                        chatId: data.chatId,
                        externalUserId: data.externalUserId,
                        timestamp: data.timestamp,
                        text: data.text,
                        status: MessageStatus.CREATED,
                    },
                ])
            )
        } else {
            handleNewMessageInClosedChat(data)
        }
    }

    const handleNewMessageInClosedChat = (data: WebsocketChatMessage) => {
        const chat = chats.find((chat) => chat.chatId === data.chatId)

        if (!chat) {
            if (data.chatId && role) {
                void dispatch(createChatWithNewMessage(data.chatId, role))
            }
            return
        }

        const updatedChat = {
            ...chat,
            unreadCount: chat.unreadCount + 1,
            lastMessageTimestamp: new Date().toISOString(),
        }
        dispatch(updateChat(updatedChat))
        fetchChatList()
    }

    return (
        <Layout fitHeight={true}>
            <div className={styles.container}>
                <ChatHeader />
                <div className={styles.content}>
                    <div className={styles.chats}>
                        {!isLoading && chats.length > 0 && (
                            <>
                                {!(isTabletOrMobile && activeChatId) && (
                                    <div className={styles.usersListWrapper}>
                                        <div
                                            className={styles.usersListHeader}
                                            onClick={() => window.history.back()}
                                        >
                                            <BackButton />
                                        </div>
                                        <UsersList chats={chats} />
                                    </div>
                                )}
                                {!(isTabletOrMobile && !activeChatId) && <DialogSide />}
                            </>
                        )}
                        {!isLoading && !chats.length && <NoChat />}
                    </div>
                    {isLoading && (
                        <div className={styles.spinnerWrapper}>
                            <Spinner fullscreen={true} />
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    )
}
